<script lang="ts">
  import type { SkillLogContext } from '$lib/componentTypes';
  import { getContext, onMount } from 'svelte';
  import BottomNextBtn from './BottomNextBtn.svelte';
  import { proficiencyLevelDescription, proficiencyLevelIcon, proficiencyLevelLabel } from '$lib/util';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import BackBtn from '../common/BackBtn.svelte';
  import { goto } from '$app/navigation';
  import type { LeaderboardSkillsDetailsSkillItem } from '$lib/api/types';
  import { page } from '$app/state';

  interface Props {
    classId: number;
    skillId: number;
    lbSkill?: LeaderboardSkillsDetailsSkillItem;
  }

  let { lbSkill, skillId, classId }: Props = $props();

  const isDirect = page.url.searchParams.has('direct');

  if (!lbSkill) {
    goto(`/classes/${classId}/log`);
  }

  let {
    setOrderedSelectedSkills,
    getSelectedSkills,
    getProficiencyLevel,
    setProficiencyLevel,
    postSkillLog,
    getIsPosting,
  } = getContext<SkillLogContext>('skillLog');
  let isPosting = $derived(getIsPosting());
  let selectedSkills = $derived(isDirect ? [skillId] : getSelectedSkills());
  let selectedSkillIdx = $derived(isDirect ? 0 : selectedSkills.indexOf(skillId));

  onMount(() => {
    if (isDirect) {
      setOrderedSelectedSkills([skillId]);
    }
  });

  $effect(() => {
    if (selectedSkills.length === 0) {
      goto(`/classes/${classId}/log`);
    }
  });

  let selfAssessmentLevel = $state(getProficiencyLevel(skillId) ?? lbSkill?.lastRecordedProficiency ?? 1);
</script>

{#if lbSkill}
  <BackBtn />

  <form
    onsubmit={e => {
      e.preventDefault();
      setProficiencyLevel(skillId, selfAssessmentLevel);

      if (selectedSkillIdx === selectedSkills.length - 1) {
        postSkillLog();
      } else {
        goto(`/classes/${classId}/log/${selectedSkills[selectedSkillIdx + 1]}`);
      }
    }}
  >
    <div class="row">
      <div class="col col-12 col-md-8 col-lg-6 m-auto">
        <h2>{lbSkill.skillName}</h2>
        <label for="self_assessment" class="mb-3">
          How proficient did you feel completing this skill? Please self-evaluate your proficiency level.
        </label>

        {#if lbSkill.lastRecordedProficiency}
          <h3 class="subtle-header">Last recorded proficiency level</h3>
          <p>
            <FontAwesomeIcon icon={proficiencyLevelIcon(lbSkill.lastRecordedProficiency)} />
            {proficiencyLevelLabel(lbSkill.lastRecordedProficiency)}
          </p>
        {/if}

        <div class="d-flex gap-3 fs-2 justify-content-between">
          {#snippet proficiencyLevelIconButton(level: number)}
            <button
              type="button"
              class="btn icon-button"
              onclick={() => (selfAssessmentLevel = level)}
              aria-label={proficiencyLevelLabel(level)}
              title={proficiencyLevelLabel(level)}
            >
              <FontAwesomeIcon icon={proficiencyLevelIcon(level)} />
            </button>
          {/snippet}
          {@render proficiencyLevelIconButton(1)}
          {@render proficiencyLevelIconButton(2)}
          {@render proficiencyLevelIconButton(3)}
          {@render proficiencyLevelIconButton(4)}
        </div>

        <input
          type="range"
          id="self_assessment"
          name="selfAssessment"
          bind:value={selfAssessmentLevel}
          class="form-range"
          min="1"
          max="4"
        />

        <div aria-live="polite" class="proficiency-level-description">
          <h3 class="fs-6 mb-0">{proficiencyLevelLabel(selfAssessmentLevel)}</h3>
          <p>{proficiencyLevelDescription(selfAssessmentLevel)}</p>
        </div>

        {#if selectedSkills.length > 1}
          <p class="small text-secondary text-center mt-5">
            {selectedSkillIdx + 1} of {selectedSkills.length} skills selected
          </p>
        {/if}
      </div>
    </div>

    <BottomNextBtn
      lastBtn={selectedSkillIdx === selectedSkills.length - 1}
      disabled={isPosting}
      isSubmitting={isPosting}
    />
  </form>
{/if}

<style>
  .proficiency-level-description {
    min-height: 6lh;
  }

  .icon-button {
    --bs-btn-hover-color: var(--uams-primary-red-hover);
    --bs-btn-padding-x: 0;
    --bs-btn-padding-y: 0;
    --bs-btn-font-size: 1.5rem;
    --bs-btn-color: var(--bs-primary);
    --bs-btn-border-radius: 2.5rem;
  }

  .form-range {
    --slider-height: 2rem;
    margin: 0.5rem 0;
    height: var(--slider-height);
  }

  .form-range::-webkit-slider-thumb {
    background-color: var(--bs-primary);
    height: var(--slider-height);
    width: var(--slider-height);
    border-radius: var(--slider-height);
    margin: 0;
  }

  .form-range::-webkit-slider-runnable-track {
    height: var(--slider-height);
    border-radius: var(--slider-height);
  }

  .form-range::-webkit-slider-thumb:active {
    background-color: var(--uams-primary-red-hover);
  }
</style>
